<template>
  <div
    class="product-section mb-5"
    :class="{'offer-rejected': rejected}"
  >
    <!-- Product Header -->
    <div class="product-header p-3 mb-3 rounded">
      <div class="d-flex align-items-center mb-2">
        <i class="fa fa-cube text-primary mr-2"></i>
        <h6 class="mb-0 font-weight-bold">{{ product.name }}</h6>
      </div>

      <div class="product-note text-muted mb-2">
        <small>
          <i class="fa fa-info-circle mr-1"></i>
          {{ $t('spesifikasiyaHaqqndaQeydlr') }} {{ product.note || '-' }}
        </small>
      </div>

      <div 
        v-if="priceDetail.alternativeOffer"
        class="alternative-product-badge mt-2"
      >
        <span class="badge badge-primary py-1 px-2">
          <i class="fa fa-exchange mr-1"></i>
          {{ $t('detailedComparisonModal.nameOfAlternative') }}: {{ priceDetail.alternativeOffer.productName }}
        </span>
      </div>
    </div>

    <!-- Product and Alternative Product Tables -->
    <div class="row">
      <div class="col-md-6">
        <div class="price-table-container mb-3">
          <h6 class="price-table-title mb-2">
            <i class="fa fa-table mr-2"></i>
            {{ $t('detailedComparisonModal.priceDetails') || 'Qiymət detalları' }}
          </h6>

          <div class="table-responsive">
            <table class="table table-bordered table-hover price-comparison-table">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('detailedComparisonModal.productPrice') }}</th>
                  <th>{{ $t('detailedComparisonModal.edv') }}</th>
                  <th>{{ $t('detailedComparisonModal.sum') }}</th>
                  <th>{{ $t('detailedComparisonModal.global') }}</th>
                </tr>
              </thead>
              <tbody>
                <!-- MAIN PRODUCT OFFER ROW -->
                <tr
                  v-if="priceDetail.noOffer"
                  class="main-offer-row no-offer-row"
                >
                  <td colspan="4" class="text-center">
                    <div class="no-offer-message">
                      <span class="no-offer-badge">Təklif yoxdur</span>
                      <small class="d-block text-muted mt-1">Bu məhsul üçün təklif verilməyib</small>
                    </div>
                  </td>
                </tr>
                <tr
                  v-else
                  :class="[
                    'main-offer-row', 
                    priceDetail.isSelected ? 'selected-offer' : '',
                    mainRank === 1 ? 'best-rank' : ''
                  ]"
                  @mouseenter="$emit('show-tooltip', $t('detailedComparisonModal.selectOffer'))"
                  @mouseleave="$emit('hide-tooltip')"
                  @click="$emit('select-offer', offerId, priceDetail.product, 'main')"
                >
                  <td>
                    <div class="price-cell">
                      <div class="price-value">{{ priceDetail.price }} AZN</div>
                      <small class="text-muted">
                        x {{ product.count }}
                        {{ product.unit }}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="price-cell">
                      <div class="price-value">
                        {{
                          isVat(priceDetail)
                            ? ((priceDetail.price * 0.18) * product.count).toFixed(2)
                            : 0
                        }} AZN
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="price-cell">
                      <div class="price-value">
                        {{
                          isVat(priceDetail)
                            ? (((priceDetail.price * 0.18) + priceDetail.price) * product.count).toFixed(2)
                            : priceDetail.price * product.count
                        }} AZN
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="rank-badge" :class="{'rank-1': mainRank === 1}">
                      {{ mainRank }}
                    </div>
                  </td>
                </tr>

                <!-- ALTERNATIVE PRODUCT OFFER ROW -->
                <tr
                  v-if="priceDetail.alternativeOffer && !priceDetail.noOffer"
                  :class="[
                    'alternative-offer-row', 
                    priceDetail.alternativeOffer?.isSelected ? 'selected-offer' : '',
                    alternativeRank === 1 ? 'best-rank' : ''
                  ]"
                  @mouseenter="$emit('show-tooltip', $t('detailedComparisonModal.selectOffer'))"
                  @mouseleave="$emit('hide-tooltip')"
                  @click="$emit('select-offer', offerId, priceDetail.product, 'alternative')"
                >
                  <td>
                    <div class="price-cell">
                      <div class="price-value text-primary">{{ priceDetail.alternativeOffer.price }} AZN</div>
                      <small class="text-muted">
                        x {{ product.count }}
                        {{ product.unit }}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="price-cell">
                      <div class="price-value text-primary">
                        {{
                          isVat(priceDetail.alternativeOffer)
                            ? ((priceDetail.alternativeOffer.price * 0.18) * product.count).toFixed(2)
                            : 0
                        }} AZN
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="price-cell">
                      <div class="price-value text-primary">
                        {{
                          isVat(priceDetail.alternativeOffer)
                            ? ((priceDetail.alternativeOffer.price * 0.18) + (priceDetail.alternativeOffer.price) * product.count).toFixed(2)
                            : (priceDetail.alternativeOffer.price * product.count).toFixed(2)
                        }} AZN
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="rank-badge" :class="{'rank-1': alternativeRank === 1}">
                      {{ alternativeRank }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Product Requirements Section -->
      <div class="col-md-6">
        <div class="specs-container mb-3">
          <h6 class="specs-title mb-2">
            <i class="fa fa-file-text-o mr-2"></i>
            {{ $t('spesifikasiyaHaqqndaQeydlr') }}
          </h6>

          <div class="specs-content p-3 mb-3 rounded">
            <div class="specs-text wrap-text">
              {{ priceDetail.techSpecification || '-' }}
            </div>
          </div>

          <div 
            v-if="priceDetail.alternativeOffer"
            class="alternative-specs-content p-3 mb-3 rounded"
          >
            <div class="d-flex align-items-center mb-2">
              <i class="fa fa-exchange text-primary mr-2"></i>
              <h6 class="mb-0 text-primary">{{ $t('detailedComparisonModal.alternateSpecification') }}</h6>
            </div>
            <div class="specs-text wrap-text">
              {{ priceDetail.alternativeOffer?.techSpecs || '-' }}
            </div>
          </div>

          <div class="requirements-section mb-3">
            <h6 class="requirements-title mb-2">
              <i class="fa fa-check-square-o mr-2"></i>
              {{ $t('newAUCRequest.specialRequirements') }}
            </h6>

            <div class="table-responsive">
              <table class="table table-bordered table-sm table-hover requirements-table mb-0">
                <colgroup>
                  <col style="width: 60px;">
                  <col>
                  <col style="width: 80px;">
                </colgroup>
                <thead class="thead-light">
                  <tr>
                    <th scope="col" class="text-center">#</th>
                    <th scope="col" class="text-start">{{ $t('detailedComparisonModal.teleb') }}</th>
                    <th scope="col" class="text-center">{{ $t('detailedComparisonModal.cavab') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(req, idx) in product.requirements"
                    :key="req.id"
                  >
                    <td class="text-center align-middle">
                      <span class="req-number">{{ idx + 1 }}</span>
                    </td>
                    <td class="text-start align-middle">
                      {{ req.requirement }}
                    </td>
                    <td class="text-center align-middle">
                      <span 
                        class="req-reply"
                        :class="{'text-success': getRequirementReply(priceDetail, req.id) === 'Bəli'}"
                      >
                        {{ getRequirementReply(priceDetail, req.id) || '-' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Files Section -->
        <div
          v-if="priceDetail.techSpecsFiles && priceDetail.techSpecsFiles.length"
          class="files-section mb-3"
        >
          <h6 class="files-title mb-2">
            <i class="fa fa-paperclip mr-2"></i>
            {{ $t('detailedComparisonModal.attachedFiles') || 'Əlavə edilmiş fayllar' }}
          </h6>

          <div class="files-container p-3 rounded">
            <div class="d-flex flex-wrap">
              <div
                v-for="(f, ii) in priceDetail.techSpecsFiles"
                :key="ii"
                class="file-item mr-3 mb-3"
              >
                <a
                  v-if="isPhoto(f)"
                  target="_blank"
                  :href="getPhoto(f, '')"
                  class="file-link"
                >
                  <div class="image-preview">
                    <img
                      :src="getPhoto(f, 'thumbnailCropped')"
                      class="img-fluid rounded"
                      :alt="$t('newRequest.requestPhoto')"
                    >
                  </div>
                  <div class="file-name mt-1 text-center">
                    <small>{{ f.split('/').pop().substring(0, 10) }}{{ f.split('/').pop().length > 10 ? '...' : '' }}</small>
                  </div>
                </a>
                <a
                  v-else
                  target="_blank"
                  :href="getPhoto(f, '')"
                  class="file-link"
                >
                  <div class="file-box">
                    <div class="file-icon">
                      <i class="fa" :class="getFileIcon(f)"></i>
                    </div>
                    <div class="file-extension">
                      {{ f.split('.').pop().toUpperCase() }}
                    </div>
                  </div>
                  <div class="file-name mt-1 text-center">
                    <small>{{ f.split('/').pop().substring(0, 10) }}{{ f.split('/').pop().length > 10 ? '...' : '' }}</small>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Alternative Offer Files Section -->
        <div
          v-if="priceDetail.techSpecsFilesAlternative"
          class="files-section mb-3"
        >
          <h6 class="files-title mb-2 text-primary">
            <i class="fa fa-paperclip mr-2"/>
            <i class="fa fa-exchange mr-2"/>
            {{ $t('detailedComparisonModal.alternativeAttachedFiles') || 'Alternativ təklif faylları' }}
          </h6>

          <div class="files-container p-3 rounded alternative-files-container">
            <div class="d-flex flex-wrap">
              <div
                v-for="(f, ii) in priceDetail.techSpecsFilesAlternative"
                :key="ii"
                class="file-item mr-3 mb-3"
              >
                <a
                  v-if="isPhoto(f)"
                  target="_blank"
                  :href="getPhoto(f, '')"
                  class="file-link"
                >
                  <div class="image-preview">
                    <img
                      :src="getPhoto(f, 'thumbnailCropped')"
                      class="img-fluid rounded"
                      :alt="$t('newRequest.requestPhoto')"
                    >
                  </div>
                  <div class="file-name mt-1 text-center">
                    <small>{{ f.split('/').pop().substring(0, 10) }}{{ f.split('/').pop().length > 10 ? '...' : '' }}</small>
                  </div>
                </a>
                <a
                  v-else
                  target="_blank"
                  :href="getPhoto(f, '')"
                  class="file-link"
                >
                  <div class="file-box">
                    <div class="file-icon">
                      <i class="fa" :class="getFileIcon(f)"></i>
                    </div>
                    <div class="file-extension">
                      {{ f.split('.').pop().toUpperCase() }}
                    </div>
                  </div>
                  <div class="file-name mt-1 text-center">
                    <small>{{ f.split('/').pop().substring(0, 10) }}{{ f.split('/').pop().length > 10 ? '...' : '' }}</small>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductComparisonSection",
  props: {
    priceDetail: {
      type: Object,
      required: true
    },
    product: {
      type: Object,
      required: true
    },
    offerId: {
      type: Number,
      required: true
    },
    mainRank: {
      type: Number,
      required: true
    },
    alternativeRank: {
      type: Number,
      default: 0
    },
    rejected: {
      type: Boolean,
      default: false
    },
    getRequirementReply: {
      type: Function,
      required: true
    },
    isPhoto: {
      type: Function,
      required: true
    },
    getPhoto: {
      type: Function,
      required: true
    }
  },
  methods: {
    isVat(offer) {
      return offer.isVatPayer === 'true';
    },
    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase();

      // Map common file extensions to Font Awesome icons
      const iconMap = {
        'pdf': 'fa-file-pdf-o',
        'doc': 'fa-file-word-o',
        'docx': 'fa-file-word-o',
        'xls': 'fa-file-excel-o',
        'xlsx': 'fa-file-excel-o',
        'ppt': 'fa-file-powerpoint-o',
        'pptx': 'fa-file-powerpoint-o',
        'txt': 'fa-file-text-o',
        'zip': 'fa-file-archive-o',
        'rar': 'fa-file-archive-o',
        'jpg': 'fa-file-image-o',
        'jpeg': 'fa-file-image-o',
        'png': 'fa-file-image-o',
        'gif': 'fa-file-image-o'
      };

      return iconMap[extension] || 'fa-file-o';
    }
  }
};
</script>

<style scoped>
/* Product Section */
.product-header {
  background-color: #f1f8ff;
  border-left: 4px solid #007bff;
}

.product-note {
  font-size: 0.9rem;
}

/* Price Table */
.price-table-title {
  color: #495057;
  font-weight: 600;
}

.price-comparison-table {
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.price-comparison-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.price-cell {
  padding: 0.25rem 0;
}

.price-value {
  font-weight: 600;
  font-size: 1rem;
}

.main-offer-row:hover, .alternative-offer-row:hover {
  background-color: #f8f9fa;
  cursor: pointer;
}

.selected-offer {
  background-color: #fff3cd !important;
}

.best-rank {
  background-color: #e8f5e9 !important;
}

.rank-badge {
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #6c757d;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rank-1 {
  background-color: #28a745;
}

/* Specs Section */
.specs-title, .files-title, .requirements-title {
  color: #495057;
  font-weight: 600;
}

.specs-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.alternative-specs-content {
  background-color: #f1f8ff;
  border: 1px solid #cfe2ff;
}

.specs-text {
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Requirements Table */
.requirements-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.req-number {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #495057;
  font-weight: 600;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.req-reply {
  font-weight: 600;
}

/* Files Section */
.files-container {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.alternative-files-container {
  background-color: #f1f8ff;
  border: 1px solid #cfe2ff;
}

.file-item {
  transition: all 0.2s ease;
}

.file-item:hover {
  transform: translateY(-2px);
}

.file-link {
  text-decoration: none;
  color: inherit;
}

.image-preview {
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.file-box {
  width: 80px;
  height: 80px;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-icon {
  font-size: 1.5rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.file-extension {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.file-name {
  font-size: 0.8rem;
  color: #6c757d;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.offer-rejected {
  background-color: #f9f9f9;
  opacity: 0.75;
}

/* No Offer Styling */
.no-offer-row {
  background-color: #f8f9fa;
}

.no-offer-message {
  padding: 15px 0;
}

.no-offer-badge {
  display: inline-block;
  background-color: #6c757d;
  color: white;
  font-size: 0.85rem;
  padding: 5px 15px;
  border-radius: 12px;
  font-weight: 500;
}

.wrap-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
